import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trash2, Plus, Search, AlertCircle, DollarSign, RotateCcw, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABEL = { return: 'Devolución', exchange: 'Cambio', warranty: 'Garantía' };
const REASON_LABEL = { change_of_mind: 'Cambió de opinión', does_not_fit: 'No sirve / No le queda' };

export default function ReturnDetail() {
  const { id } = useParams();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [registerOpen, setRegisterOpen] = useState(true);
  // Inventory search for adding items
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/returns/${id}`, { headers: getAuthHeaders() });
      setDoc(res.data);
      // Load original sale to know max returnable qty per line
      if (res.data?.original_sale_id) {
        try {
          const s = await axios.get(`${API}/api/pos/sales/${res.data.original_sale_id}`, { headers: getAuthHeaders() });
          setSale(s.data);
        } catch { /* noop — validation will still run server-side */ }
      }
    } catch { navigate('/sales'); }
    setLoading(false);
  };

  useEffect(() => {
    if (!doc?.branch_id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/cash-register/status?branch_id=${doc.branch_id}`, { headers: getAuthHeaders() });
        if (!cancelled) setRegisterOpen(!!r.data?.is_open);
      } catch { if (!cancelled) setRegisterOpen(true); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [doc?.branch_id]);

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Cargando...</div>;
  if (!doc) return null;

  const isDraft = doc.status === 'draft';
  const isExchange = (doc.added_items || []).length > 0;
  const effectiveType = isExchange && doc.type !== 'warranty' ? 'exchange' : doc.type;

  // Build {sale-item-key -> originally purchased qty} so we can cap returned
  // quantities at the qty bought in the linked sale.
  const purchasedQty = {};
  (sale?.items || []).forEach((it, i) => {
    const q = parseFloat(it.quantity) || 0;
    purchasedQty[`idx-${i}`] = q;
    if (it.id) purchasedQty[it.id] = q;
  });
  const maxForLine = (it) => purchasedQty[it?.id] ?? (parseFloat(it?.quantity) || 1);

  const persist = async (changes) => {
    setError(''); setSaving(true);
    try {
      const res = await axios.put(`${API}/api/returns/${id}`, changes, { headers: getAuthHeaders() });
      setDoc(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo guardar');
    }
    setSaving(false);
  };

  const removeSelectedItem = (idx) => {
    if (!isDraft) return;
    const next = (doc.selected_items || []).filter((_, i) => i !== idx);
    if (next.length === 0) {
      setError('Debe quedar al menos un artículo seleccionado');
      return;
    }
    persist({ selected_items: next });
  };

  const updateSelectedQty = (idx, qty) => {
    if (!isDraft) return;
    const current = (doc.selected_items || [])[idx];
    if (!current) return;
    const max = maxForLine(current);
    const parsed = Math.max(1, parseFloat(qty) || 1);
    if (parsed > max) {
      setError(`No puedes devolver ${parsed} de '${current.name}'. Máximo permitido: ${max} (cantidad vendida).`);
      return;
    }
    const next = (doc.selected_items || []).map((it, i) => {
      if (i !== idx) return it;
      return { ...it, quantity: parsed, subtotal: Math.round(parsed * (it.price || 0) * 100) / 100 };
    });
    persist({ selected_items: next });
  };

  const searchInventory = async () => {
    if (!search.trim()) { setResults([]); return; }
    try {
      const r = await axios.get(`${API}/api/inventory?search=${encodeURIComponent(search)}`, { headers: getAuthHeaders() });
      setResults(r.data.filter(i => i.active !== false));
    } catch { setResults([]); }
  };

  const addInventoryItem = (inv) => {
    if (!isDraft) return;
    const existing = doc.added_items || [];
    const found = existing.find(x => x.inventory_id === inv.id);
    let next;
    if (found) {
      next = existing.map(x => x.inventory_id === inv.id
        ? { ...x, quantity: x.quantity + 1, subtotal: Math.round((x.quantity + 1) * (x.price || 0) * 100) / 100 }
        : x);
    } else {
      next = [...existing, {
        id: `${inv.id}-${Date.now()}`,
        inventory_id: inv.id,
        name: inv.name,
        sku: inv.sku,
        quantity: 1,
        price: inv.sale_price || 0,
        subtotal: inv.sale_price || 0,
      }];
    }
    setSearch(''); setResults([]);
    persist({ added_items: next });
  };

  const removeAdded = (lineId) => {
    if (!isDraft) return;
    persist({ added_items: (doc.added_items || []).filter(x => x.id !== lineId) });
  };

  const updateAddedQty = (lineId, qty) => {
    if (!isDraft) return;
    const next = (doc.added_items || []).map(x => {
      if (x.id !== lineId) return x;
      const q = Math.max(1, parseFloat(qty) || 1);
      return { ...x, quantity: q, subtotal: Math.round(q * (x.price || 0) * 100) / 100 };
    });
    persist({ added_items: next });
  };

  const complete = async () => {
    setError('');
    // Final-submission client guard
    for (const it of (doc.selected_items || [])) {
      const max = maxForLine(it);
      if ((parseFloat(it.quantity) || 0) > max) {
        setError(`No puedes devolver ${it.quantity} de '${it.name}'. Máximo permitido: ${max} (cantidad vendida).`);
        return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/api/returns/${id}/complete`, { method: 'Efectivo' }, { headers: getAuthHeaders() });
      setDoc(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo completar el registro');
    }
    setSaving(false);
  };

  const resolution = doc.resolution_amount || 0;
  const needsMoney = Math.abs(resolution) > 0.005;
  const blockedByRegister = needsMoney && !registerOpen;

  return (
    <div data-testid="return-detail-page">
      <Link to={`/sales/${doc.original_sale_id}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1D3C6E] mb-4 no-underline">
        <ArrowLeft className="w-4 h-4" /> Volver a la venta {doc.original_sale_ticket || ''}
      </Link>

      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">
              {TYPE_LABEL[effectiveType] || effectiveType}
            </h1>
            <span className={`status-badge ${doc.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
              {doc.status === 'completed' ? 'Completado' : 'Borrador'}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {doc.client_name || 'Cliente general'} · {new Date(doc.created_at).toLocaleString('es-MX')} · Atiende {doc.processed_by || '—'}
          </p>
          {doc.reason && <p className="text-xs text-slate-500 mt-0.5">Motivo: {REASON_LABEL[doc.reason] || doc.reason}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Returned items */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Artículos a devolver</h3>
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
                <tr><th className="text-left py-2">Articulo</th><th className="text-right py-2 w-20">Cant.</th><th className="text-right py-2 w-24">Precio</th><th className="text-right py-2 w-24">Subtotal</th><th className="w-8"></th></tr>
              </thead>
              <tbody>
                {(doc.selected_items || []).map((it, idx) => (
                  <tr key={it.id || idx} className="border-b border-slate-100">
                    <td className="py-2 font-medium">{it.name}</td>
                    <td className="py-2 text-right">
                      {isDraft ? (
                        <input type="number" min={1} max={maxForLine(it)} value={it.quantity}
                          onChange={(e) => updateSelectedQty(idx, e.target.value)}
                          className="w-16 text-right border border-slate-200 rounded px-1.5 py-0.5 text-sm"
                          data-testid={`return-sel-qty-${idx}`} />
                      ) : it.quantity}
                      <span className="text-[10px] text-slate-400 ml-1">/ {maxForLine(it)}</span>
                    </td>
                    <td className="py-2 text-right text-slate-600">${(it.price || 0).toFixed(2)}</td>
                    <td className="py-2 text-right font-medium">${(it.subtotal || 0).toFixed(2)}</td>
                    <td className="py-2 text-right">
                      {isDraft && (
                        <button onClick={() => removeSelectedItem(idx)} className="w-7 h-7 rounded-full bg-red-50 hover:bg-red-100 inline-flex items-center justify-center" data-testid={`return-remove-sel-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between text-sm border-t border-slate-200 mt-2 pt-2">
              <span className="text-slate-500">Total devuelto</span>
              <span className="font-semibold text-rose-600">-${(doc.returned_total || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Added items (for exchange / replacement under warranty) */}
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900 font-['Outfit']">Artículos a entregar (opcional)</h3>
              <span className="text-xs text-slate-400">{(doc.added_items || []).length} items</span>
            </div>
            {isDraft && (
              <div className="flex gap-2 mb-3">
                <input type="text" placeholder="Buscar artículo en inventario..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchInventory()}
                  className="flex-1 h-9 px-3 border border-slate-200 rounded-md text-sm" data-testid="return-search" />
                <button onClick={searchInventory} className="h-9 px-3 rounded-md border border-slate-200 hover:bg-slate-50">
                  <Search className="w-4 h-4" />
                </button>
              </div>
            )}
            {results.length > 0 && (
              <div className="mb-3 max-h-40 overflow-y-auto border border-slate-200 rounded-lg">
                {results.map(inv => (
                  <button key={inv.id} onClick={() => addInventoryItem(inv)}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0 text-left">
                    <div>
                      <p className="text-sm font-medium">{inv.name}</p>
                      <p className="text-xs text-slate-500">{inv.sku} · Stock {inv.stock} · ${(inv.sale_price || 0).toFixed(2)}</p>
                    </div>
                    <Plus className="w-4 h-4 text-[#AFBD20]" />
                  </button>
                ))}
              </div>
            )}
            {(doc.added_items || []).length === 0 ? (
              <p className="text-xs text-slate-400">Sin artículos agregados. Si el cliente solo devuelve, deja vacío.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase text-slate-500">
                  <tr><th className="text-left py-2">Articulo</th><th className="text-right py-2 w-20">Cant.</th><th className="text-right py-2 w-24">Precio</th><th className="text-right py-2 w-24">Subtotal</th><th className="w-8"></th></tr>
                </thead>
                <tbody>
                  {(doc.added_items || []).map((it) => (
                    <tr key={it.id} className="border-b border-slate-100">
                      <td className="py-2 font-medium">{it.name}</td>
                      <td className="py-2 text-right">
                        {isDraft ? (
                          <input type="number" min={1} value={it.quantity}
                            onChange={(e) => updateAddedQty(it.id, e.target.value)}
                            className="w-16 text-right border border-slate-200 rounded px-1.5 py-0.5 text-sm" />
                        ) : it.quantity}
                      </td>
                      <td className="py-2 text-right text-slate-600">${(it.price || 0).toFixed(2)}</td>
                      <td className="py-2 text-right font-medium">${(it.subtotal || 0).toFixed(2)}</td>
                      <td className="py-2 text-right">
                        {isDraft && (
                          <button onClick={() => removeAdded(it.id)} className="w-7 h-7 rounded-full bg-red-50 hover:bg-red-100 inline-flex items-center justify-center">
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(doc.added_items || []).length > 0 && (
              <div className="flex justify-between text-sm border-t border-slate-200 mt-2 pt-2">
                <span className="text-slate-500">Total entregado</span>
                <span className="font-semibold text-emerald-600">${(doc.added_total || 0).toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar — Resolution */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-3 font-['Outfit']">Resolución</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Devuelto</span><span className="text-rose-600">-${(doc.returned_total || 0).toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Entregado</span><span className="text-emerald-600">${(doc.added_total || 0).toFixed(2)}</span></div>
              <div className="border-t border-slate-200 pt-2 flex justify-between text-base font-bold">
                <span>{resolution > 0 ? 'Cliente paga' : resolution < 0 ? 'Reembolso al cliente' : 'Sin diferencia'}</span>
                <span className={resolution > 0 ? 'text-[#1D3C6E]' : resolution < 0 ? 'text-rose-600' : 'text-slate-600'}>
                  {resolution < 0 ? '-' : ''}${Math.abs(resolution).toFixed(2)}
                </span>
              </div>
            </div>

            {isDraft ? (
              <>
                {blockedByRegister && (
                  <div className="mt-3 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="return-register-closed">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>La caja está cerrada. Abre la caja antes de continuar.</span>
                  </div>
                )}
                {resolution > 0 ? (
                  <button onClick={complete}
                    disabled={blockedByRegister || saving}
                    className="mt-3 w-full h-10 rounded-md bg-[#1D3C6E] hover:bg-[#152B50] text-white text-sm font-medium inline-flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="return-pay-btn">
                    <DollarSign className="w-4 h-4" /> Pagar ${Math.abs(resolution).toFixed(2)}
                  </button>
                ) : resolution < 0 ? (
                  <button onClick={complete}
                    disabled={blockedByRegister || saving}
                    className="mt-3 w-full h-10 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium inline-flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="return-refund-btn">
                    <RotateCcw className="w-4 h-4" /> Reembolsar ${Math.abs(resolution).toFixed(2)}
                  </button>
                ) : (
                  <button onClick={complete}
                    disabled={saving}
                    className="mt-3 w-full h-10 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center justify-center gap-1 disabled:opacity-50"
                    data-testid="return-complete-btn">
                    <CheckCircle2 className="w-4 h-4" /> Completar sin movimiento
                  </button>
                )}
                {error && <p className="text-xs text-rose-600 mt-2" data-testid="return-error">{error}</p>}
              </>
            ) : (
              <div className="mt-3 flex items-start gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Finalizado el {new Date(doc.completed_at).toLocaleString('es-MX')} por {doc.completed_by}</span>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-900 mb-2 font-['Outfit']">Venta original</h3>
            <Link to={`/sales/${doc.original_sale_id}`} className="text-sm font-medium text-[#1D3C6E] hover:underline no-underline">
              {doc.original_sale_ticket || doc.original_sale_id.slice(-8)}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
